/**
 * KYC Queue — live review queue backed by /admin/v1/kyc-cases.
 */
import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { PageHead } from "../components/PageHead";
import { EmptyState, ErrorCard, LoadingCard } from "../components/Async";
import { useAdminQuery, useDebouncedValue, shortDateTime, errorMessage } from "../lib/admin";
import { adminApi } from "../lib/api";
import type { KycCase, KycDetail } from "../lib/types";
import {
  Search,
  Eye,
  Check,
  X,
  Clock,
  CheckCircle2,
  AlertTriangle,
  ShieldCheck,
  MessageSquare,
} from "lucide-react";

type Tab = "pending" | "approved" | "rejected";

interface KycList {
  tab: Tab;
  total: number;
  counts: { pending: number; approved: number; rejected: number };
  data: KycCase[];
}

function statusIcon(status: string) {
  if (status === "approved") return <CheckCircle2 size={14} style={{ color: "var(--ev-mint-500)" }} />;
  if (status === "rejected") return <X size={14} style={{ color: "var(--ev-red-500)" }} />;
  return <Clock size={14} style={{ color: "var(--ev-amber-500)" }} />;
}

export function KYC() {
  const queryClient = useQueryClient();
  const [tab, setTab] = useState<Tab>("pending");
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState<KycCase | null>(null);
  const [note, setNote] = useState("");
  const [actionError, setActionError] = useState<string | null>(null);

  const q = useDebouncedValue(search);
  const list = useAdminQuery<KycList>(["kyc", tab, q], "/admin/v1/kyc-cases", {
    tab,
    q: q || undefined,
  });
  const detail = useAdminQuery<KycDetail>(
    ["kyc-case", selected?.ref],
    `/admin/v1/kyc-cases/${selected?.ref}`,
    {},
    Boolean(selected),
  );

  const decide = useMutation({
    mutationFn: (input: { ref: string; decision: "approve" | "reject" | "request_info"; note: string }) =>
      adminApi.post(`/admin/v1/kyc-cases/${input.ref}/decision`, undefined, {
        query: { decision: input.decision, note: input.note },
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["admin"] });
      setSelected(null);
      setNote("");
      setActionError(null);
    },
    onError: (error) => setActionError(errorMessage(error)),
  });

  const counts = list.data?.counts;

  return (
    <>
      <PageHead
        title="KYC Queue"
        description="Know Your Customer verification and approvals."
        actions={
          counts ? (
            <span className="ev-badge ev-badge-amber">
              <Clock size={12} /> {counts.pending} pending
            </span>
          ) : undefined
        }
      />

      <div className="admin-card-header admin-filter-bar" style={{ marginBottom: 16 }}>
        <div className="admin-toggle-group">
          {(["pending", "approved", "rejected"] as const).map((t) => (
            <button
              key={t}
              className={`admin-toggle ${tab === t ? "admin-toggle-active" : ""}`}
              onClick={() => { setTab(t); setSelected(null); }}
            >
              {t.charAt(0).toUpperCase() + t.slice(1)}
              {counts ? ` (${counts[t]})` : ""}
            </button>
          ))}
        </div>
        <div style={{ position: "relative" }}>
          <Search size={16} style={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)", color: "var(--ev-slate-400)", pointerEvents: "none" }} />
          <input
            className="ev-input"
            placeholder="Search business or code…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{ paddingLeft: 36, width: 260 }}
          />
        </div>
      </div>

      {actionError && (
        <div className="admin-note" style={{ marginBottom: 12 }}>
          <AlertTriangle size={16} />
          <span>{actionError}</span>
        </div>
      )}

      {list.isLoading && <LoadingCard label="Loading KYC queue…" />}
      {list.error && <ErrorCard error={list.error} onRetry={() => void list.refetch()} />}
      {list.data && list.data.data.length === 0 && <EmptyState>No {tab} KYC cases.</EmptyState>}

      {list.data && list.data.data.length > 0 && (
        <div className="admin-card" style={{ padding: 0, overflow: "hidden" }}>
          <div className="admin-table-wrap">
            <table className="admin-table">
              <thead>
                <tr>
                  <th className="admin-table-th">Case</th>
                  <th className="admin-table-th">Business</th>
                  <th className="admin-table-th">Phone</th>
                  <th className="admin-table-th">Region</th>
                  <th className="admin-table-th">Submitted</th>
                  <th className="admin-table-th">Risk</th>
                  <th className="admin-table-th">Status</th>
                  <th className="admin-table-th" />
                </tr>
              </thead>
              <tbody>
                {list.data.data.map((c) => (
                  <tr key={c.ref} className="admin-table-row-hover">
                    <td className="admin-table-td" data-label="Case"><span className="ev-body-mono">{c.agent_code}</span></td>
                    <td className="admin-table-td" data-label="Business">
                      <div className="admin-cell-stack">
                        <span className="ev-body-semibold">{c.business_name || "—"}</span>
                        <span className="ev-body-micro muted">{c.owner_name}</span>
                      </div>
                    </td>
                    <td className="admin-table-td" data-label="Phone"><span className="ev-body-mono">{c.phone}</span></td>
                    <td className="admin-table-td" data-label="Region"><span className="ev-body">{c.region || "—"}</span></td>
                    <td className="admin-table-td" data-label="Submitted">
                      <span className="ev-body-micro">{shortDateTime(c.submitted_at)}</span>
                    </td>
                    <td className="admin-table-td" data-label="Risk">
                      <span className={`ev-badge ${c.risk === "low" ? "ev-badge-mint" : "ev-badge-amber"}`}>{c.risk}</span>
                    </td>
                    <td className="admin-table-td" data-label="Status">
                      <span className="admin-status">
                        {statusIcon(c.status)}
                        <span style={{ textTransform: "capitalize" }}>{c.status.replace("_", " ")}</span>
                      </span>
                    </td>
                    <td className="admin-table-td" data-label="">
                      <button className="ev-btn ev-btn-sm ev-btn-ghost" onClick={() => { setSelected(c); setNote(""); }}>
                        <Eye size={16} /> Review
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {selected && (
        <div className="admin-drawer-overlay" onClick={() => setSelected(null)}>
          <div className="admin-drawer" onClick={(e) => e.stopPropagation()} style={{ width: 520, maxWidth: "100%" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
              <div>
                <h3 style={{ marginBottom: 4 }}>{selected.business_name || selected.agent_code}</h3>
                <span className="ev-body-micro muted">{selected.ref}</span>
              </div>
              <button className="ev-icon-btn" onClick={() => setSelected(null)} aria-label="Close">
                <X size={20} />
              </button>
            </div>

            {detail.isLoading && <div style={{ marginTop: 24 }}><LoadingCard label="Loading case…" /></div>}
            {detail.error && <div style={{ marginTop: 24 }}><ErrorCard error={detail.error} /></div>}

            {(detail.data ?? selected) && (
              <div style={{ marginTop: 24 }}>
                <div className="admin-detail-grid">
                  <div>
                    <div className="ev-body-micro muted">Phone</div>
                    <div className="ev-body-mono">{selected.phone}</div>
                  </div>
                  <div>
                    <div className="ev-body-micro muted">Region</div>
                    <div className="ev-body">{selected.region || "—"}</div>
                  </div>
                  <div>
                    <div className="ev-body-micro muted">Submitted</div>
                    <div className="ev-body">{shortDateTime(selected.submitted_at)}</div>
                  </div>
                  <div>
                    <div className="ev-body-micro muted">Risk</div>
                    <div className="ev-body">{selected.risk}</div>
                  </div>
                </div>

                <h4 style={{ marginTop: 24, marginBottom: 8 }}>Timeline</h4>
                <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                  {(detail.data?.timeline ?? []).length === 0 && (
                    <span className="ev-body-micro muted">No audit events yet.</span>
                  )}
                  {(detail.data?.timeline ?? []).slice(0, 10).map((event) => (
                    <div key={event.id}>
                      <div className="ev-body-micro">{event.action.replaceAll(".", " · ").replaceAll("_", " ")}</div>
                      <div className="ev-body-micro muted">{shortDateTime(event.occurred_at)}</div>
                    </div>
                  ))}
                </div>

                {selected.status !== "approved" && selected.status !== "rejected" && (
                  <>
                    <h4 style={{ marginTop: 24, marginBottom: 8 }}>
                      <MessageSquare size={14} /> Review note
                    </h4>
                    <textarea
                      className="ev-textarea"
                      rows={3}
                      placeholder="Reason or instructions for the agent…"
                      value={note}
                      onChange={(e) => setNote(e.target.value)}
                      style={{ width: "100%", resize: "vertical" }}
                    />
                    <div style={{ display: "flex", gap: 8, marginTop: 16, flexWrap: "wrap" }}>
                      <button
                        className="ev-btn ev-btn-primary"
                        disabled={decide.isPending}
                        onClick={() => decide.mutate({ ref: selected.ref, decision: "approve", note })}
                      >
                        <Check size={16} /> Approve
                      </button>
                      <button
                        className="ev-btn ev-btn-outline"
                        disabled={decide.isPending}
                        onClick={() => decide.mutate({ ref: selected.ref, decision: "request_info", note })}
                      >
                        <ShieldCheck size={16} /> Request info
                      </button>
                      <button
                        className="ev-btn ev-btn-outline"
                        disabled={decide.isPending}
                        onClick={() => decide.mutate({ ref: selected.ref, decision: "reject", note })}
                      >
                        <X size={16} /> Reject
                      </button>
                    </div>
                  </>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}
